/**
 * The server's write check for Audits.KpiSummary (security 2026-10-26):
 * Flow 4's KPIs email reads the column with json() / float(), which THROW on
 * a malformed row — one bad visit would fail that month's KPIs email for
 * every district. The app reads it defensively (kpis.ts parseKpiSummary);
 * the server refuses what the app never writes and STORES A REWRITTEN COPY
 * ({v, n} only, rebuilt from the parsed values): no duplicate or case-clash
 * keys, no extra (deep) keys, no huge numbers reach the flow.
 * No imports: the api shares it.
 */

/** Most KPI values one summary may carry (a template has a handful; a forged row can't flood the page). */
export const MAX_KPIS = 200;
/** Longest question id a summary may carry (template ids are ~20 characters). */
export const MAX_KPI_ID = 100;
/** Larger values are left out of a summary (no real KPI is near it; huge numbers are fragile in the flow). */
export const MAX_KPI_VALUE = 1e15;
/** Template question ids (validateTemplate ID_PATTERN) — lower case, so no two differ by case only. */
const ID = /^[a-z0-9][a-z0-9_-]*$/;

export type KpiSummaryCheck = { problem: string } | { value: string | null };

/**
 * The summary to store, or why it is refused. Empty stays as sent. Every
 * problem names "KpiSummary": on a 400 that names it, the sync's header
 * write retries without the column (graphTransport writeHeader), so an
 * audit is never stuck behind its summary.
 */
export function checkKpiSummary(raw: unknown): KpiSummaryCheck {
  if (raw === null || raw === undefined || raw === '') return { value: raw === '' ? '' : null };
  if (typeof raw !== 'string') return { problem: 'KpiSummary must be JSON text.' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { problem: 'KpiSummary must be JSON.' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { problem: 'KpiSummary must be an object.' };
  if ((parsed as { v?: unknown }).v !== 1) return { problem: 'KpiSummary must have v: 1.' };
  const n = (parsed as { n?: unknown }).n;
  if (!n || typeof n !== 'object' || Array.isArray(n)) return { problem: 'KpiSummary "n" must be an object.' };
  const entries = Object.entries(n as Record<string, unknown>);
  if (entries.length > MAX_KPIS) return { problem: 'KpiSummary has too many KPI values.' };
  const out: Record<string, number> = {};
  for (const [key, value] of entries) {
    if (!ID.test(key) || key.length > MAX_KPI_ID) return { problem: `KpiSummary has a bad KPI id "${key.slice(0, 40)}".` };
    if (typeof value !== 'number' || !Number.isFinite(value)) return { problem: `KpiSummary: KPI "${key}" must be a number.` };
    if (Math.abs(value) <= MAX_KPI_VALUE) out[key] = value;
  }
  return { value: JSON.stringify({ v: 1, n: out }) };
}
